import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { GUID, PersonOrg, PersonOrgType, UnencryptedData } from '@ndvr/customer-api';
import { AccountType, PortfolioTaxType } from '@ndvr/portfolio-api';

import { getClientTypeAlias, getPersonInitials, getPersonName, titleCase } from 'utils';
import { Avatar } from 'widgets/Avatar';
import {
    IconBuilding,
    IconCashPayment,
    IconHeartRibbon,
    IconMoneyEgg,
    IconPersonCircle,
    IconPersons,
    IconPiggyBank,
    IconSavingBank,
    IconShield,
    IconSize,
} from 'widgets/icon';

import { DiamondAvatar } from './Avatar';
import { HorizontalLayout, VerticalLayout } from './Layout';
import { Text, TextLink } from './Text';

const HORIZONTAL_GAP = 16;
const VERTICAL_GAP = 4;

export const HouseholdLogoCard = ({ name, to }: { name: string; to: string }) => {
    return (
        <HorizontalLayout $rowCenter $gap={HORIZONTAL_GAP}>
            <DiamondAvatar size={28}>{name.substring(0, 1)}</DiamondAvatar>
            <VerticalLayout $gap={VERTICAL_GAP}>
                <TextLink onClick={(ev) => ev.stopPropagation()} to={to}>
                    {name}
                </TextLink>
            </VerticalLayout>
        </HorizontalLayout>
    );
};

interface PersonLogoCardProps {
    personInfo?: {
        firstName?: string | UnencryptedData;
        lastName?: string | UnencryptedData;
        orgName?: UnencryptedData;
        primaryEmail?: string | UnencryptedData;
        personOrgGUID?: GUID<PersonOrg>;
    };
}

const FALLBACK_INITIAL = 'A';

export const PersonLogoCard = ({
    to,
    personInfo,
}: PersonLogoCardProps & {
    to?: string;
}) => {
    const displayName = personInfo?.orgName?.plainText || getPersonName(personInfo);
    const initials =
        personInfo?.orgName?.plainText?.substring(0, 1) || getPersonInitials(personInfo) || FALLBACK_INITIAL;

    return (
        <HorizontalLayout $rowCenter $gap={HORIZONTAL_GAP}>
            <Avatar>{initials}</Avatar>
            <VerticalLayout $gap={VERTICAL_GAP}>
                <TextLink
                    onClick={(ev: React.MouseEvent) => ev.stopPropagation()}
                    to={to ?? (personInfo?.personOrgGUID ? `/person/${personInfo?.personOrgGUID}` : '')}
                >
                    <Text helper $themeColor="primary">
                        {displayName}
                    </Text>
                </TextLink>
                {personInfo?.primaryEmail ? (
                    <Text helper $themeColor="subtle">
                        {typeof personInfo?.primaryEmail === 'string'
                            ? personInfo?.primaryEmail
                            : personInfo?.primaryEmail?.plainText}
                    </Text>
                ) : null}
            </VerticalLayout>
        </HorizontalLayout>
    );
};

interface PersonOrgLogoCardProps {
    name: ReactNode;
    personOrgType: PersonOrgType;
    to: string;
}

export const PersonOrgLogoCard = ({ name, personOrgType, to }: PersonOrgLogoCardProps) => {
    const clientAlias = getClientTypeAlias(personOrgType);
    return (
        <HorizontalLayout $rowCenter $gap={HORIZONTAL_GAP}>
            <Avatar>
                <PersonOrgIconContainer $center $rowCenter>
                    <PersonOrgIcon personOrgType={personOrgType} />
                </PersonOrgIconContainer>
            </Avatar>
            <VerticalLayout $gap={VERTICAL_GAP}>
                <TextLink onClick={(ev) => ev.stopPropagation()} to={to}>
                    {name}
                </TextLink>
                {clientAlias ? (
                    <Text helper $themeColor="subtle">
                        {clientAlias}
                    </Text>
                ) : null}
            </VerticalLayout>
        </HorizontalLayout>
    );
};

const PersonOrgIconContainer = styled(HorizontalLayout)`
    margin-top: 0.2rem;
    width: 20px;
    height: 20px;
`;

export const PersonOrgIcon = ({
    personOrgType,
    size = 'xSmall',
}: {
    personOrgType: PersonOrgType;
    size?: IconSize;
}) => {
    if (personOrgType === PersonOrgType.ORGANIZATION) return <IconBuilding size={size} />;
    if (personOrgType === PersonOrgType.TRUST) return <IconShield size={size} />;
    if (personOrgType === PersonOrgType.JOINT_PERSONS) return <IconPersons size={size} />;
    return <IconPersonCircle size={size} />;
};

interface PortfolioLogoCardProps {
    name: string;
    portfolioTaxType: PortfolioTaxType;
    to: string;
}

export const PortfolioLogoCard = ({ name, portfolioTaxType, to }: PortfolioLogoCardProps) => {
    return (
        <HorizontalLayout $rowCenter $gap={HORIZONTAL_GAP}>
            <PortfolioLogo $columnCenter $rowCenter>
                <PortfolioTaxProfileIcon portfolioTaxType={portfolioTaxType} />
            </PortfolioLogo>
            <VerticalLayout $gap={VERTICAL_GAP}>
                <TextLink onClick={(ev) => ev.stopPropagation()} to={to}>
                    {name}
                </TextLink>
                <Text helper $themeColor="subtle">
                    {titleCase(portfolioTaxType)}
                </Text>
            </VerticalLayout>
        </HorizontalLayout>
    );
};

const PortfolioLogo = styled(VerticalLayout)`
    border: 1px solid ${({ theme }) => theme.colors.cocoa200};
    border-radius: 3px;
    padding: 8px;
`;

const PortfolioTaxProfileIcon = ({ portfolioTaxType }: { portfolioTaxType: PortfolioTaxType }) => {
    if (portfolioTaxType === PortfolioTaxType.TAXABLE) return <IconSavingBank size="micro" />;
    if (portfolioTaxType === PortfolioTaxType.TRADITIONAL_IRA) return <IconPiggyBank size="micro" />;
    if (portfolioTaxType === PortfolioTaxType.SEP_IRA) return <IconCashPayment size="micro" />;
    if (portfolioTaxType === PortfolioTaxType.TAX_EXEMPT) return <IconHeartRibbon size="micro" />;
    if (
        portfolioTaxType === PortfolioTaxType.ROTH_IRA ||
        portfolioTaxType === PortfolioTaxType.TRADITIONAL_401K ||
        portfolioTaxType === PortfolioTaxType.ROTH_401K
    ) {
        return <IconMoneyEgg size="micro" />;
    }
    return <IconSavingBank size="micro" />;
};

interface AccountLogoCardProps {
    name: ReactNode;
    accountType: AccountType;
    to: string;
}

export const AccountLogoCard = ({ name, accountType, to }: AccountLogoCardProps) => {
    return (
        <HorizontalLayout $rowCenter $gap={HORIZONTAL_GAP}>
            <AccountLogo $columnCenter $rowCenter>
                <AccountTypeProfileIcon accountType={accountType} />
            </AccountLogo>
            <VerticalLayout $gap={VERTICAL_GAP}>
                <TextLink onClick={(ev) => ev.stopPropagation()} to={to}>
                    {name}
                </TextLink>
                <Text helper $themeColor="subtle">
                    {titleCase(accountType)}
                </Text>
            </VerticalLayout>
        </HorizontalLayout>
    );
};

const AccountLogo = styled(VerticalLayout)`
    border: 1px solid ${({ theme }) => theme.colors.cocoa200};
    border-radius: 3px;
    padding: 8px;
`;

const AccountTypeProfileIcon = ({ accountType }: { accountType: AccountType }) => {
    if (accountType === AccountType.TAXABLE) return <IconSavingBank size="micro" />;
    if (accountType === AccountType.TRADITIONAL_IRA) return <IconPiggyBank size="micro" />;
    if (accountType === AccountType.SEP_IRA) return <IconCashPayment size="micro" />;
    if (accountType === AccountType.TAX_EXEMPT) return <IconHeartRibbon size="micro" />;
    if (
        accountType === AccountType.ROTH_IRA ||
        accountType === AccountType.TRADITIONAL_401K ||
        accountType === AccountType.ROTH_401K
    ) {
        return <IconMoneyEgg size="micro" />;
    }
    return <IconSavingBank size="micro" />;
};
